import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  sendVipTransferFormFailedEvent,
  sendVipTransferFormRequestedEvent,
  sendVipTransferFormStartedEvent,
} from '@/modules/googleAnalytics';
import {
  buildVipFailedData,
  toFailedSlot,
} from '@/modules/vip/Steps/components/VipTransfer/components/TransferRequestCard/components/VipTransferFormCard/analytics';
import type { IVipFileError } from '@/modules/vip/Steps/components/VipTransfer/components/TransferRequestCard/components/VipTransferFormCard/components/VipFileUploader/helpers';
import type { IVipTransferFormProps } from '@/modules/vip/Steps/components/VipTransfer/components/TransferRequestCard/components/VipTransferFormCard/types';
import { useVipTransferRequest } from '@/modules/vip/Steps/components/VipTransfer/components/TransferRequestCard/hooks/useVipTransferRequest';
import { validateScreenshots } from './validation';

interface IUseVipTransferFormProps extends IVipTransferFormProps {}

export const useVipTransferForm = ({ onSuccess }: IUseVipTransferFormProps) => {
  const { t } = useTranslation('vip');
  const { sendRequest, isRequestPending } = useVipTransferRequest();

  const [emailScreenshot, setEmailScreenshot] = useState<File | null>(null);
  const [statusScreenshot, setStatusScreenshot] = useState<File | null>(null);
  const [emailError, setEmailError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [emailFileError, setEmailFileError] = useState<IVipFileError | null>(
    null
  );
  const [statusFileError, setStatusFileError] = useState<IVipFileError | null>(
    null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const hasStartedRef = useRef(false);

  const trackFormStarted = useCallback(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    sendVipTransferFormStartedEvent();
  }, []);

  const handleEmailChange = useCallback(
    (file: File | null) => {
      setEmailScreenshot(file);
      if (file) {
        setEmailError('');
        trackFormStarted();
      }
    },
    [trackFormStarted]
  );

  const handleStatusChange = useCallback(
    (file: File | null) => {
      setStatusScreenshot(file);
      if (file) {
        setStatusError('');
        trackFormStarted();
      }
    },
    [trackFormStarted]
  );

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);

    const filesRequiredKey =
      'steps.vipTransfer.transferForm.errorFilesRequired';

    const errors = validateScreenshots(
      emailScreenshot,
      statusScreenshot,
      t(filesRequiredKey)
    );

    setEmailError(errors.emailError);
    setStatusError(errors.statusError);

    const file1 = toFailedSlot(emailScreenshot, emailFileError);
    const file2 = toFailedSlot(statusScreenshot, statusFileError);

    const clientFailedData = buildVipFailedData({ file1, file2 });

    if (clientFailedData.error_data.length) {
      sendVipTransferFormFailedEvent(clientFailedData);
      return;
    }

    const files = [emailScreenshot, statusScreenshot].filter(
      (f): f is File => f !== null
    );

    const result = await sendRequest(files);

    if (result.status === 'success') {
      sendVipTransferFormRequestedEvent();
      onSuccess();
      return;
    }

    sendVipTransferFormFailedEvent(
      buildVipFailedData({
        file1,
        file2,
        serverError: { code: result.code, rawError: result.rawError },
      })
    );
    setSubmitError(result.error);
  }, [
    emailScreenshot,
    statusScreenshot,
    emailFileError,
    statusFileError,
    onSuccess,
    sendRequest,
    t,
  ]);

  return {
    emailScreenshot,
    statusScreenshot,
    emailError,
    statusError,
    submitError,
    isRequestPending,
    handleEmailChange,
    handleStatusChange,
    handleEmailFileError: setEmailFileError,
    handleStatusFileError: setStatusFileError,
    handleSubmit,
  };
};
